import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchStoneSizes,
  deleteStoneSize,
  setStoneSizeEdit,
  clearStoneSizeEdit,
} from '../../../../redux/actions/masters/stoneAttribute/stoneSize/stoneSizeAction';
import dayjs from 'dayjs';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const StoneSizeTable = ({ onNew, sieveSizes = [] }) => {
  const dispatch = useDispatch();
  const { list = [], loading, error } = useSelector((state) => state.stoneSize);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  useEffect(() => {
    dispatch(fetchStoneSizes());
  }, [dispatch]);

  // Function to get sieve name from code
  const getSieveName = (sieveCode) => {
    const sieve = sieveSizes.find(s => s.sieve_size_code === sieveCode);
    return sieve ? sieve.sieve_size_name : sieveCode;
  };

  const handleEdit = async (stoneSize) => {
    await showInfo('Edit Stone Size', `Editing stone size: ${stoneSize.stone_size_name}`);
    dispatch(setStoneSizeEdit(stoneSize));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm(
      'Delete Stone Size',
      `Are you sure you want to delete "${name}"? This action cannot be undone.`
    );
    if (confirmed) {
      dispatch(deleteStoneSize(id));
    }
  };

  const handleNew = () => {
    dispatch(clearStoneSizeEdit());
    onNew();
  };

  const filteredStoneSizes = list.filter(stoneSize =>
    stoneSize.stone_size_name && stoneSize.stone_size_name.toLowerCase().includes(search.toLowerCase()) && stoneSize.trash === 0
  );

  const columns = [
    {
      name: 'Stone Size Code',
      selector: row => row.stone_size_code,
      sortable: true,
    },
    {
      name: 'Stone Size Name',
      selector: row => row.stone_size_name,
      sortable: true,
    },
    {
      name: 'Sieve Size Name',
      selector: row => getSieveName(row.sieve_size),
      sortable: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.stone_size_id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.stone_size_id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.stone_size_id, row.stone_size_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.stone_size_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.stone_size_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        .stone-size-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .stone-size-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .stone-size-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Stone Size List</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredStoneSizes}
        pagination
        highlightOnHover
        striped
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Stone Sizes found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
        className="stone-size-table"
      />
      {error && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default StoneSizeTable; 